'use client';

import { useMinLoading } from '@/hooks/use-min-loading';
import { useLocaleNavigation } from '@/hooks/use-locale-navigation';
import Link from 'next/link';
import { type ComponentProps, type MouseEvent, useEffect, useRef, useState } from 'react';

interface LocaleAwareLinkProps extends Omit<ComponentProps<typeof Link>, 'href'> {
  href: string;
  disabled?: boolean;
  loading?: boolean;
  minLoadingMs?: number;
}

const EXTERNAL_URL_PATTERN = /^(?:https?:\/\/|mailto:|tel:)/i;

const isModifiedEvent = (event: MouseEvent<HTMLAnchorElement>) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

/**
 * Component Link với locale-aware routing
 *
 * @example
 * // Thay vì:
 * <Link href="/signup">Sign Up</Link>
 *
 * // Sử dụng:
 * <LocaleAwareLink href="/signup">Sign Up</LocaleAwareLink>
 *
 * // Khi đang ở /login -> href sẽ là /signup
 * // Khi đang ở /vi/login -> href sẽ là /vi/signup
 * // Khi đang ở /ar/login -> href sẽ là /ar/signup
 */
export default function LocaleAwareLink({
  href,
  children,
  className,
  disabled = false,
  loading = false,
  minLoadingMs = 600,
  prefetch = false,
  onClick,
  ...props
}: LocaleAwareLinkProps) {
  const { navigate, navigateTo } = useLocaleNavigation();
  const [isNavigating, setIsNavigating] = useState(false);
  const navigationTimerRef = useRef<number | null>(null);
  const link = navigate(href);
  const visibleLoading = useMinLoading(loading || isNavigating, minLoadingMs);
  const blocked = disabled || visibleLoading;

  useEffect(
    () => () => {
      if (navigationTimerRef.current) {
        window.clearTimeout(navigationTimerRef.current);
      }
    },
    [],
  );

  if (blocked) {
    return (
      <span
        aria-busy={visibleLoading || undefined}
        aria-disabled="true"
        className={`${className || ''} cursor-not-allowed opacity-50 pointer-events-none`}
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      href={link}
      className={className}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;

        if (
          props.target === '_blank' ||
          EXTERNAL_URL_PATTERN.test(href) ||
          isModifiedEvent(event)
        ) {
          return;
        }

        event.preventDefault();
        setIsNavigating(true);

        navigationTimerRef.current = window.setTimeout(() => {
          navigateTo(href);
          setIsNavigating(false);
        }, minLoadingMs);
      }}
      prefetch={prefetch}
      {...props}
    >
      {children}
    </Link>
  );
}
